import { Injectable, inject } from '@angular/core';
import { take } from 'rxjs/operators';

import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { FeatureToggleService } from 'ish-core/feature-toggle.module';
import { loadProductPrices } from 'ish-core/store/shopping/product-prices';
import { whenTruthy } from 'ish-core/utils/operators';

import { CrossSellingPopupService } from '../services/cross-selling-popup.service';

/**
 * Extended ShoppingFacade that intercepts addProductToBasket calls
 * to check for cross-selling products
 */
@Injectable({ providedIn: 'root' })
export class ExtendedShoppingFacade extends ShoppingFacade {
  private popupService = inject(CrossSellingPopupService);
  private featureToggleService = inject(FeatureToggleService);

  override addProductToBasket(sku: string, quantity: number, warrantySku?: string) {
    // Call parent implementation first
    super.addProductToBasket(sku, quantity, warrantySku);

    // Check for cross-selling products only if feature is enabled
    this.featureToggleService
      .enabled$('crossSellingPopup')
      .pipe(take(1))
      .subscribe(isEnabled => {
        if (isEnabled) {
          // Add small delay to ensure the product is fully added before checking
          setTimeout(() => this.checkCrossSellingProducts(sku, quantity), 100);
        }
      });
  }

  private checkCrossSellingProducts(sku: string, quantity: number) {
    this.productLinks$(sku)
      .pipe(
        whenTruthy(), // Wait for links to actually load, skip initial undefined
        take(1)
      )
      .subscribe(links => {
        if (links?.crossselling?.products?.length) {
          // Load price for the added product via parent's store
          // eslint-disable-next-line @typescript-eslint/dot-notation
          this['store'].dispatch(loadProductPrices({ skus: [sku] }));

          this.popupService.emitCrossSellingEvent({
            sku,
            productLinks: links,
            timestamp: Date.now(),
            quantity,
          });
        }
      });
  }
}
